import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  Plus,
} from "lucide-react";
import { useState } from "react";
import { useOutletContext } from "react-router-dom";

import AppointmentDetailModal from "@/components/AppointmentDetailModal";
import NewAppointmentModal from "@/components/NewAppointmentModal";
import {
  StatusBadge,
  Button,
  Card,
  EmptyState,
  Select,
  Spinner,
} from "@/components/ui";
import {
  addDays,
  addMonths,
  endOfMonth,
  isoDate,
  monthGrid,
  startOfMonth,
  startOfWeek,
} from "@/lib/format";
import { useFormat, useT, type Chave } from "@/lib/i18n";
import { useAppointmentAction, useAppointments, useProfessionals } from "@/lib/queries";
import type { Appointment, Me } from "@/lib/types";

/** Ações possíveis a partir de cada estado — espelha TRANSITIONS do backend.
 *
 *  O rótulo é uma CHAVE, não o texto: a tabela é constante de módulo e
 *  seria avaliada uma vez, no import, congelando o idioma daquele
 *  instante. A tradução acontece no JSX, a cada render. */
const ACTIONS: Record<string, { action: string; label: Chave; variant?: "danger" }[]> = {
  pending: [
    { action: "confirm", label: "action.confirm" },
    { action: "cancel", label: "action.cancel", variant: "danger" },
  ],
  confirmed: [
    { action: "start", label: "action.start" },
    { action: "no-show", label: "action.noShow", variant: "danger" },
    { action: "cancel", label: "action.cancel", variant: "danger" },
  ],
  in_progress: [{ action: "complete", label: "action.complete" }],
};

type Vista = "day" | "week" | "month";

const VISTAS: { vista: Vista; label: Chave }[] = [
  { vista: "day", label: "agenda.viewDay" },
  { vista: "week", label: "agenda.viewWeek" },
  { vista: "month", label: "agenda.viewMonth" },
];

/** Rótulos das setas por vista — quem usa leitor de tela precisa ouvir
 *  para onde o botão anda, e ele anda coisas diferentes em cada uma. */
const SETAS: Record<Vista, { anterior: Chave; proximo: Chave }> = {
  day: { anterior: "agenda.prevDay", proximo: "agenda.nextDay" },
  week: { anterior: "agenda.prevWeek", proximo: "agenda.nextWeek" },
  month: { anterior: "agenda.prevMonth", proximo: "agenda.nextMonth" },
};

/** Dois intervalos, e eles não são o mesmo.
 *
 *  `busca` é o que se pede à API; `periodo` é do que a tela fala. Só
 *  divergem no mês: a grade começa no domingo e termina no sábado, então
 *  ela exibe até seis dias do mês vizinho — e esses dias precisam vir
 *  preenchidos, senão as pontas do calendário mentem dizendo que não há
 *  nada marcado. Mas os cartões de resumo continuam somando o MÊS: um
 *  "previsto no período" que inclui o dia 30 do mês passado não bate com
 *  nenhum relatório e não serve para decidir nada.
 */
function intervalo(vista: Vista, data: Date) {
  if (vista === "day") return { busca: { de: data, ate: data }, periodo: { de: data, ate: data } };
  if (vista === "week") {
    const de = startOfWeek(data);
    const faixa = { de, ate: addDays(de, 6) };
    return { busca: faixa, periodo: faixa };
  }
  const grade = monthGrid(data);
  return {
    busca: { de: grade[0], ate: grade[grade.length - 1] },
    periodo: { de: startOfMonth(data), ate: endOfMonth(data) },
  };
}

/** O dia a que o atendimento pertence na tela.
 *
 *  Lido do `Date` e não recortado da string ISO de propósito: o horário
 *  ao lado sai de `hhmm()`, que interpreta o instante no fuso do
 *  navegador. Agrupar por outro critério colocaria um atendimento das
 *  21:00 na célula de um dia e o relógio dele no outro — a mesma linha
 *  se contradizendo. Enquanto os dois lêem o mesmo `Date`, a grade e o
 *  relógio contam a mesma história. */
const diaDe = (a: Appointment) => isoDate(new Date(a.starts_at));

const ATIVO = (a: Appointment) => !["canceled", "no_show"].includes(a.status);

export default function Agenda() {
  const { money, fullDate, monthLabel, rangeLabel } = useFormat();
  const t = useT();
  const me = useOutletContext<Me>();
  const [vista, setVista] = useState<Vista>("day");
  const [date, setDate] = useState(new Date());
  const [professional, setProfessional] = useState<number | "">("");
  const [creating, setCreating] = useState(false);
  /** O atendimento aberto na ficha. Guardamos o ID e não o objeto: a
   *  lista é refeita a cada mutação (salvar laudo, subir foto), e um
   *  objeto congelado deixaria a ficha mostrando a versão anterior de si
   *  mesma logo depois de salvar. */
  const [openId, setOpenId] = useState<number | null>(null);

  const day = isoDate(date);
  const { busca, periodo } = intervalo(vista, date);

  const { data: professionals } = useProfessionals();
  const { data, isLoading } = useAppointments({
    date_from: isoDate(busca.de),
    date_to: isoDate(busca.ate),
    professional,
  });
  const act = useAppointmentAction();

  const appointments = data?.results ?? [];

  /** O resumo fala do período, não da busca — ver `intervalo()`. */
  const deIso = isoDate(periodo.de);
  const ateIso = isoDate(periodo.ate);
  const noPeriodo = appointments.filter((a) => diaDe(a) >= deIso && diaDe(a) <= ateIso);
  const active = noPeriodo.filter(ATIVO);
  const faturamento = active.reduce((sum, a) => sum + Number(a.total_price), 0);

  const andar = (passo: number) =>
    setDate(
      vista === "month"
        ? addMonths(date, passo)
        : addDays(date, vista === "week" ? passo * 7 : passo),
    );

  /** Abrir um dia é trocar de vista junto: as ações de cada atendimento
   *  (confirmar, iniciar, faltou) só existem na lista do dia, e clicar
   *  numa célula do calendário é justamente o gesto de quem quer agir
   *  sobre aquele dia. */
  const abrirDia = (d: Date) => {
    setDate(d);
    setVista("day");
  };

  const subtitulo =
    vista === "day"
      ? fullDate(date.toISOString())
      : vista === "week"
        ? rangeLabel(periodo.de, periodo.ate)
        : monthLabel(date);

  return (
    <div className="p-4 lg:p-8">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold lg:text-2xl">{t("agenda.title")}</h1>
          <p className="text-sm text-steel-muted first-letter:uppercase">{subtitulo}</p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus size={18} />
          {t("agenda.new")}
        </Button>
      </div>

      {/* navegação do período */}
      <Card className="mb-4 flex flex-wrap items-center gap-2 p-3">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => andar(-1)}
          aria-label={t(SETAS[vista].anterior)}
        >
          <ChevronLeft size={16} />
        </Button>
        <input
          type="date"
          value={day}
          onChange={(e) => setDate(new Date(`${e.target.value}T12:00:00`))}
          className="rounded-[4px] border border-line px-3 py-1.5 text-sm"
        />
        <Button
          variant="secondary"
          size="sm"
          onClick={() => andar(1)}
          aria-label={t(SETAS[vista].proximo)}
        >
          <ChevronRight size={16} />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setDate(new Date())}>
          {t("agenda.todayShort")}
        </Button>

        {/* dia · semana · mês */}
        <div className="flex overflow-hidden rounded-[2px] border border-line">
          {VISTAS.map((v) => (
            <button
              key={v.vista}
              type="button"
              onClick={() => setVista(v.vista)}
              aria-pressed={vista === v.vista}
              className={[
                "px-3 py-2 font-display text-[11px] font-semibold uppercase tracking-[0.12em] transition",
                "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-nitro-500",
                vista === v.vista
                  ? "bg-carbon-700 text-steel"
                  : "text-steel-muted hover:bg-carbon-700/50 hover:text-steel",
              ].join(" ")}
            >
              {t(v.label)}
            </button>
          ))}
        </div>

        {me.permissions.see_all_agendas && (
          <div className="ml-auto w-full sm:w-52">
            <Select
              value={professional}
              onChange={(e) =>
                setProfessional(e.target.value ? Number(e.target.value) : "")
              }
            >
              <option value="">{t("agenda.allTechs")}</option>
              {professionals?.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.display_name}
                </option>
              ))}
            </Select>
          </div>
        )}
      </Card>

      {/* resumo do período */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Card className="p-4">
          <p className="text-xs text-steel-muted">{t("agenda.appointments")}</p>
          <p className="mt-1 text-2xl font-semibold">{active.length}</p>
        </Card>
        {me.permissions.see_financials && (
          <Card className="p-4">
            <p className="text-xs text-steel-muted">
              {t(vista === "day" ? "agenda.expected" : "agenda.expectedPeriod")}
            </p>
            <p className="mt-1 text-2xl font-semibold">{money(faturamento)}</p>
          </Card>
        )}
        <Card className="p-4">
          <p className="text-xs text-steel-muted">{t("agenda.canceledNoShows")}</p>
          <p className="mt-1 text-2xl font-semibold">
            {noPeriodo.length - active.length}
          </p>
        </Card>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-8" />
        </div>
      ) : vista === "day" ? (
        appointments.length === 0 ? (
          <Card>
            <EmptyState
              icon={<CalendarDays size={40} />}
              title={t("agenda.emptyTitle")}
              description={t("agenda.emptyText")}
              action={
                <Button onClick={() => setCreating(true)}>
                  <Plus size={18} />
                  {t("agenda.new")}
                </Button>
              }
            />
          </Card>
        ) : (
          <div className="space-y-2">
            {appointments.map((a) => (
              <AppointmentRow
                key={a.id}
                appointment={a}
                onAction={(action) => act.mutate({ id: a.id, action })}
                onOpen={() => setOpenId(a.id)}
                busy={act.isPending}
              />
            ))}
          </div>
        )
      ) : vista === "week" ? (
        <WeekView inicio={periodo.de} appointments={appointments} onOpenDay={abrirDia} />
      ) : (
        <MonthView mes={date} appointments={appointments} onOpenDay={abrirDia} />
      )}

      <NewAppointmentModal
        open={creating}
        onClose={() => setCreating(false)}
        me={me}
        defaultDate={day}
      />

      <AppointmentDetailModal
        appointment={appointments.find((a) => a.id === openId) ?? null}
        me={me}
        onClose={() => setOpenId(null)}
      />
    </div>
  );
}

/** Agrupa por dia uma vez só. Sem isto, cada uma das 42 células do mês
 *  varreria a lista inteira — 42 × N a cada render, num componente que
 *  re-renderiza a cada clique de navegação. */
function porDia(appointments: Appointment[]) {
  const mapa = new Map<string, Appointment[]>();
  for (const a of appointments) {
    const chave = diaDe(a);
    const lista = mapa.get(chave);
    if (lista) lista.push(a);
    else mapa.set(chave, [a]);
  }
  return mapa;
}

/* ------------------------------------------------------------------ semana */

function WeekView({
  inicio,
  appointments,
  onOpenDay,
}: {
  inicio: Date;
  appointments: Appointment[];
  onOpenDay: (d: Date) => void;
}) {
  const mapa = porDia(appointments);
  const hoje = isoDate(new Date());
  const dias = Array.from({ length: 7 }, (_, i) => addDays(inicio, i));

  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-7">
      {dias.map((d) => {
        const iso = isoDate(d);
        const doDia = mapa.get(iso) ?? [];
        return (
          <Card key={iso} className="flex flex-col p-0">
            <DayHeader data={d} hoje={iso === hoje} total={doDia.length} onOpen={onOpenDay} />
            <div className="flex flex-col gap-1 p-2 lg:min-h-40">
              {doDia.map((a) => (
                <Chip key={a.id} appointment={a} onOpen={() => onOpenDay(d)} />
              ))}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function DayHeader({
  data,
  hoje,
  total,
  onOpen,
}: {
  data: Date;
  hoje: boolean;
  total: number;
  onOpen: (d: Date) => void;
}) {
  const { weekday, dayNumber } = useFormat();
  const t = useT();
  // `getDay()` conta a partir do domingo; `weekday()` a partir da
  // segunda — a convenção de WorkingHours.weekday no banco.
  const nome = weekday((data.getDay() + 6) % 7, "short");

  return (
    <button
      type="button"
      onClick={() => onOpen(data)}
      aria-label={t("agenda.openDay", { day: `${nome} ${dayNumber(data)}` })}
      className={[
        "flex items-center justify-between gap-2 border-b border-line px-3 py-2 text-left transition",
        "hover:bg-carbon-700 focus-visible:outline-2 focus-visible:-outline-offset-2",
        "focus-visible:outline-nitro-500",
        hoje ? "bg-nitro-500/10" : "",
      ].join(" ")}
    >
      <span className="text-xs uppercase tracking-wide text-steel-muted">{nome}</span>
      <span
        className={[
          "text-sm font-semibold tabular-nums",
          hoje ? "text-nitro-400" : "text-steel",
        ].join(" ")}
      >
        {dayNumber(data)}
      </span>
      {total > 0 && (
        <span className="rounded-full bg-carbon-700 px-1.5 text-[11px] tabular-nums text-steel-muted">
          {total}
        </span>
      )}
    </button>
  );
}

/* --------------------------------------------------------------------- mês */

/** Quantos atendimentos cabem numa célula antes de virar "+N mais".
 *
 *  Três e não "quantos couberem": a altura da célula precisa ser a mesma
 *  em toda a grade, senão um dia cheio empurra a linha inteira e o
 *  calendário deixa de ser legível de relance — que é a única coisa que
 *  a visão de mês faz melhor do que a lista. */
const CHIPS_POR_CELULA = 3;

function MonthView({
  mes,
  appointments,
  onOpenDay,
}: {
  mes: Date;
  appointments: Appointment[];
  onOpenDay: (d: Date) => void;
}) {
  const { weekdayHeaders, dayNumber } = useFormat();
  const t = useT();
  const mapa = porDia(appointments);
  const hoje = isoDate(new Date());
  const doMes = mes.getMonth();

  return (
    <Card className="overflow-hidden p-0">
      <div className="grid grid-cols-7 border-b border-line">
        {weekdayHeaders().map((nome) => (
          <div
            key={nome}
            className="truncate px-2 py-2 text-center text-[11px] uppercase tracking-wide text-steel-muted"
          >
            {nome}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {monthGrid(mes).map((d) => {
          const iso = isoDate(d);
          const doDia = mapa.get(iso) ?? [];
          const fora = d.getMonth() !== doMes;
          const sobra = doDia.length - CHIPS_POR_CELULA;

          return (
            <div
              key={iso}
              className={[
                "min-h-14 border-b border-r border-line p-1 lg:min-h-28",
                fora ? "bg-carbon-900/40" : "",
              ].join(" ")}
            >
              <button
                type="button"
                onClick={() => onOpenDay(d)}
                aria-label={t("agenda.openDay", { day: iso })}
                className={[
                  // Empilhado no celular: lado a lado, a coluna tem a
                  // largura de um dedo e "15" seguido do contador "14"
                  // vira "1514" na leitura de relance, que é a única
                  // leitura que alguém faz num calendário de mês.
                  "mb-1 flex w-full flex-col items-center gap-0.5 rounded-[2px] px-1 transition",
                  "lg:flex-row lg:items-center lg:justify-between lg:gap-1",
                  "hover:bg-carbon-700 focus-visible:outline-2 focus-visible:-outline-offset-2",
                  "focus-visible:outline-nitro-500",
                ].join(" ")}
              >
                <span
                  className={[
                    "text-xs tabular-nums",
                    iso === hoje
                      ? "rounded-full bg-nitro-500 px-1.5 font-semibold text-carbon-900"
                      : fora
                        ? "text-steel-dim"
                        : "text-steel",
                  ].join(" ")}
                >
                  {dayNumber(d)}
                </span>
                {/* No celular a célula tem a largura de um dedo: não cabe
                    chip nenhum, e o que importa ali é saber em que dia o
                    movimento está. O número diz isso. */}
                {doDia.length > 0 && (
                  <span className="rounded-full bg-carbon-700 px-1.5 text-[10px] tabular-nums text-steel-muted lg:hidden">
                    {doDia.length}
                  </span>
                )}
              </button>

              <div className="hidden flex-col gap-0.5 lg:flex">
                {doDia.slice(0, CHIPS_POR_CELULA).map((a) => (
                  <Chip key={a.id} appointment={a} compacto onOpen={() => onOpenDay(d)} />
                ))}
                {sobra > 0 && (
                  <button
                    type="button"
                    onClick={() => onOpenDay(d)}
                    className="px-1 text-left text-[11px] text-steel-muted hover:text-steel"
                  >
                    {t("agenda.morePlus", { n: sobra })}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------- chips */

/** Um atendimento em uma linha. A faixa da cor do profissional é a mesma
 *  da lista do dia — é ela que deixa a grade legível de longe quando a
 *  oficina tem quatro pessoas atendendo ao mesmo tempo. */
function Chip({
  appointment: a,
  compacto,
  onOpen,
}: {
  appointment: Appointment;
  compacto?: boolean;
  onOpen: () => void;
}) {
  const { hhmm } = useFormat();
  const cancelado = !ATIVO(a);

  return (
    <button
      type="button"
      onClick={onOpen}
      style={{ borderLeftColor: a.professional_color }}
      className={[
        "flex w-full items-center gap-1 overflow-hidden rounded-[2px] border-l-2 bg-carbon-700/60",
        "px-1.5 py-1 text-left transition hover:bg-carbon-700",
        "focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-nitro-500",
        compacto ? "text-[11px]" : "text-xs",
        // Cancelado e falta continuam na grade de propósito: o horário
        // que vagou é informação de quem vai remarcar alguém ali.
        cancelado ? "opacity-45 line-through" : "",
      ].join(" ")}
    >
      <span className="shrink-0 tabular-nums text-steel-muted">{hhmm(a.starts_at)}</span>
      <span className="truncate text-steel">{a.customer_detail.name}</span>
    </button>
  );
}

/* --------------------------------------------------------------------- dia */

function AppointmentRow({
  appointment: a,
  onAction,
  onOpen,
  busy,
}: {
  appointment: Appointment;
  onAction: (action: string) => void;
  onOpen: () => void;
  busy: boolean;
}) {
  const { money, hhmm, phone } = useFormat();
  const pais = useOutletContext<Me>().garage.region.country;
  const t = useT();
  const actions = ACTIONS[a.status] ?? [];
  const whatsapp = `https://wa.me/${a.customer_detail.phone.replace(/\D/g, "")}`;

  return (
    <Card className="overflow-hidden">
      <div className="flex">
        {/* faixa com a cor do profissional: identifica a coluna num relance */}
        <div
          className="w-1.5 shrink-0"
          style={{ backgroundColor: a.professional_color }}
          aria-hidden
        />
        <div className="flex-1 p-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            {/* O bloco de identificação é o botão que abre a ficha. Os
                botões de ação ficam FORA dele de propósito: aninhar
                <button> é HTML inválido, e "iniciar" jamais pode virar
                um clique que só abre um modal. */}
            <button
              type="button"
              onClick={onOpen}
              aria-label={t("detail.open", { name: a.customer_detail.name })}
              className="min-w-0 rounded-[2px] text-left transition hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nitro-500"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-lg font-semibold tabular-nums">
                  {hhmm(a.starts_at)}
                </span>
                <span className="text-sm text-steel-dim">–{hhmm(a.ends_at)}</span>
                <StatusBadge status={a.status} />
                {a.is_quote && (
                  <span className="rounded-[2px] border border-sky-700/60 bg-sky-950/50 px-1.5 py-0.5 font-display text-[10px] font-semibold uppercase tracking-[0.12em] text-sky-300">
                    {t("detail.quote")}
                  </span>
                )}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {/* A placa antes do nome: no pátio o que se procura é o
                    carro, e é ela que está escrita na chave pendurada. */}
                {a.vehicle_detail && (
                  <span className="plate">{a.vehicle_detail.formatted_plate}</span>
                )}
                <span className="truncate font-medium">{a.customer_detail.name}</span>
              </div>
              <p className="truncate text-sm text-steel-muted">
                {a.items.map((i) => i.service_name).join(" + ")} · {a.professional_name}
                {a.vehicle_detail && ` · ${a.vehicle_detail.label}`}
              </p>
              {a.customer_detail.no_show_count > 0 && (
                <p className="mt-1 text-xs text-red-400">
                  {t("agenda.noShowHistory", { n: a.customer_detail.no_show_count })}
                </p>
              )}
            </button>

            <div className="text-right">
              {/* Orçamento não tem preço a mostrar: o que existe é o valor
                  orçado, e ele nasce vazio — quem entrar com "R$ 0,00" na
                  linha vai ler a visita como serviço dado de graça. */}
              <p className="font-semibold">
                {a.is_quote
                  ? a.quoted_price === null
                    ? t("detail.notQuotedYet")
                    : money(a.quoted_price)
                  : money(a.total_price)}
              </p>
              <a
                href={whatsapp}
                target="_blank"
                rel="noreferrer"
                className="mt-1 inline-flex items-center gap-1 text-sm text-emerald-400 hover:underline"
              >
                <MessageCircle size={14} />
                {phone(a.customer_detail.phone, pais)}
              </a>
            </div>
          </div>

          {actions.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {actions.map((action) => (
                <Button
                  key={action.action}
                  size="sm"
                  variant={action.variant ?? "secondary"}
                  disabled={busy}
                  onClick={() => onAction(action.action)}
                >
                  {t(action.label)}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
